import { useEffect, useMemo, useState } from 'react';
import {
  Alert, Badge, Box, Button, Checkbox, Chip, Collapse, Dialog, DialogActions, DialogContent, DialogTitle, FormControl,
  IconButton, InputAdornment, InputLabel, LinearProgress, ListItemText, MenuItem, OutlinedInput, Paper,
  Select, Snackbar, Stack, Switch, FormControlLabel,
  Table, TableBody, TableCell, TableHead, TableRow, TableSortLabel, TextField, Typography, Tooltip,
  useMediaQuery, useTheme,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import SearchIcon from '@mui/icons-material/Search';
import ClearIcon from '@mui/icons-material/Clear';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import FilterListIcon from '@mui/icons-material/FilterList';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import HistoryIcon from '@mui/icons-material/History';
import CloudSyncIcon from '@mui/icons-material/CloudSync';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import { Link } from 'react-router-dom';
import { useQuotationStore } from '../../store/quotationStore';
import type { ProjectStatus, Project, OpportunityGrade, Quotation, QuotationKind } from '../../types/Quotation';
import { PROJECT_STATUSES, projectStatusLabel, OPPORTUNITY_GRADES, opportunityGradeLabel } from '../../types/Quotation';
import { format } from 'date-fns';
import { PHP, computeTotals, ioctMargin } from '../../utils/calcsheet/calc';
import { salesAccountedTotal } from '../../utils/calcsheet/salesAccounting';
import NewCalcsheetProjectDialog from './NewCalcsheetProjectDialog';
import { exportProjectListXlsx } from '../../utils/calcsheet/xlsxExport';
import { useOneDriveAuth } from '../../contexts/OneDriveAuthContext';
import { isCorporateOneDriveConfigured } from '../../config/onedriveConfig';
import {
  ensureProposalFolder,
  ensureWonExecutionLayout,
  executionProjectFolderName,
} from '../../services/onedriveFolderService';

const statusColors: Record<ProjectStatus, 'default' | 'primary' | 'success' | 'error' | 'warning' | 'info'> = {
  draft: 'default', for_review: 'info', sent: 'primary', won: 'success', lost: 'error', inactive: 'warning',
};

// Status options come from the shared PROJECT_STATUSES list in types/Quotation.ts.
const STATUS_OPTIONS: ProjectStatus[] = PROJECT_STATUSES;
// Hidden from the list by default; still reachable by explicitly ticking them in the Status filter.
const DEFAULT_HIDDEN_STATUSES: ProjectStatus[] = ['lost', 'inactive'];
const statusLabel = projectStatusLabel;

const gradeColors: Record<OpportunityGrade, 'success' | 'warning' | 'default'> = {
  A: 'success', B: 'warning', C: 'default',
};
const GRADE_OPTIONS: OpportunityGrade[] = OPPORTUNITY_GRADES;

type SortKey = 'code' | 'name' | 'customer' | 'date' | 'updatedAt' | 'status' | 'grade' | 'grandTotal' | 'margin';
type SortDir = 'asc' | 'desc';

// Last-used sort + filters persist per browser so the list reopens the way the user left it
const PREFS_KEY = 'calcsheet-projects-prefs';
const SORT_PREF_KEY = 'calcsheet-projects-sort'; // pre-filter-prefs key, still read for migration
const SORT_KEYS: SortKey[] = ['code', 'name', 'customer', 'date', 'updatedAt', 'status', 'grade', 'grandTotal', 'margin'];
const LEGACY_FILTERS = ['all', 'legacy', 'current'] as const;

type ListPrefs = {
  sortKey: SortKey;
  sortDir: SortDir;
  search: string;
  statusFilter: ProjectStatus[];
  customerFilter: string;
  yearFilter: string;
  legacyFilter: 'all' | 'legacy' | 'current';
  ongoingOnly: boolean;
  hideInactive: boolean;
};

function defaultListPrefs(): ListPrefs {
  return {
    sortKey: 'updatedAt',
    sortDir: 'desc',
    search: '',
    statusFilter: [],
    customerFilter: 'all',
    yearFilter: 'all',
    legacyFilter: 'all',
    ongoingOnly: false,
    hideInactive: true,
  };
}

function loadListPrefs(): ListPrefs {
  const defaults = defaultListPrefs();
  try {
    const raw = localStorage.getItem(PREFS_KEY);
    if (raw) {
      const parsed = JSON.parse(raw);
      return {
        sortKey: SORT_KEYS.includes(parsed.sortKey) ? parsed.sortKey : defaults.sortKey,
        sortDir: parsed.sortDir === 'asc' || parsed.sortDir === 'desc' ? parsed.sortDir : defaults.sortDir,
        search: typeof parsed.search === 'string' ? parsed.search : '',
        statusFilter: Array.isArray(parsed.statusFilter)
          ? parsed.statusFilter.filter((s: string): s is ProjectStatus =>
              (PROJECT_STATUSES as readonly string[]).includes(s))
          : [],
        customerFilter: typeof parsed.customerFilter === 'string' ? parsed.customerFilter : 'all',
        yearFilter: typeof parsed.yearFilter === 'string' ? parsed.yearFilter : 'all',
        legacyFilter: (LEGACY_FILTERS as readonly string[]).includes(parsed.legacyFilter)
          ? parsed.legacyFilter
          : 'all',
        ongoingOnly: !!parsed.ongoingOnly,
        hideInactive: parsed.hideInactive !== false,
      };
    }
    const oldSort = localStorage.getItem(SORT_PREF_KEY);
    if (oldSort) {
      const parsed = JSON.parse(oldSort);
      if (SORT_KEYS.includes(parsed.key) && (parsed.dir === 'asc' || parsed.dir === 'desc')) {
        return { ...defaults, sortKey: parsed.key, sortDir: parsed.dir };
      }
    }
  } catch { /* corrupted pref — fall through to default */ }
  return defaults;
}

function saveListPrefs(prefs: ListPrefs) {
  try {
    localStorage.setItem(PREFS_KEY, JSON.stringify(prefs));
  } catch { /* storage unavailable (private mode/quota) */ }
}

export default function Projects() {
  const projects = useQuotationStore((s) => s.projects);
  const clients = useQuotationStore((s) => s.clients);
  const quotations = useQuotationStore((s) => s.quotations);
  const deleteProject = useQuotationStore((s) => s.deleteProject);
  const updateProject = useQuotationStore((s) => s.updateProject);
  const syncMainProject = useQuotationStore((s) => s.syncMainProject);

  // OneDrive bulk auto-link
  const {
    isAuthenticated: oneDriveSignedIn,
    isLoading: oneDriveLoading,
    login: oneDriveLogin,
    getAccessToken: getOneDriveToken,
  } = useOneDriveAuth();
  const [bulkLinkDialogOpen, setBulkLinkDialogOpen] = useState(false);
  const [bulkLinkProgress, setBulkLinkProgress] = useState<{
    running: boolean;
    done: number;
    total: number;
    linked: number;
    created: number;
    failed: number;
    currentCode: string;
    failures: string[];
  } | null>(null);
  const [bulkLinkSummary, setBulkLinkSummary] = useState<string>('');
  const [createNotice, setCreateNotice] = useState<{ severity: 'success' | 'info' | 'warning' | 'error'; message: string } | null>(null);
  const [exportingList, setExportingList] = useState(false);
  const oneDriveRequired = isCorporateOneDriveConfigured();
  const [deleteTarget, setDeleteTarget] = useState<Project | null>(null);
  // Portrait phones (< 600 px) get tap-to-open cards; landscape phones, tablets and desktop keep the table.
  const theme = useTheme();
  const isCards = useMediaQuery(theme.breakpoints.down('sm'));
  const [filtersOpen, setFiltersOpen] = useState(false);

  // Projects that don't yet have a proposal folder linked. (We don't try to
  // auto-link execution folders here — those are derived from proposal folders
  // via the promote-to-execution flow when status flips to 'won'.)
  const unlinkedProjects = useMemo<Project[]>(
    () => projects.filter((p) => !p.proposalFolderId),
    [projects],
  );

  const runBulkAutoLink = async () => {
    if (!oneDriveSignedIn) {
      await oneDriveLogin();
      // loginRedirect navigates away; this code path won't actually continue.
      return;
    }
    const token = await getOneDriveToken();
    if (!token) {
      setBulkLinkSummary('Could not get OneDrive token. Sign in again.');
      setBulkLinkDialogOpen(false);
      return;
    }
    setBulkLinkDialogOpen(false);
    const targets = unlinkedProjects;
    const progress = {
      running: true,
      done: 0,
      total: targets.length,
      linked: 0,
      created: 0,
      failed: 0,
      currentCode: '',
      failures: [] as string[],
    };
    setBulkLinkProgress({ ...progress });

    // Sequential to avoid hammering Graph; each project does 2-3 API calls.
    for (const p of targets) {
      progress.currentCode = p.code;
      setBulkLinkProgress({ ...progress });
      try {
        const ref = await ensureProposalFolder(token, p);
        await updateProject(p.id, {
          proposalFolderId: ref.id,
          proposalFolderUrl: ref.webUrl,
        });
        if (ref.matchedExisting) progress.linked++;
        else progress.created++;
        // Bonus: if the project is already won, also resolve/rename the execution folder
        // to the IOCT convention (`IOCT####-CUST Name`) when a project no. is linked.
        if (p.status === 'won') {
          try {
            const { executionFolder, proposalFolder } = await ensureWonExecutionLayout(token, {
              ...p,
              proposalFolderId: ref.id,
            });
            await updateProject(p.id, {
              executionFolderId: executionFolder.id,
              executionFolderUrl: executionFolder.webUrl,
              ...(proposalFolder?.webUrl ? { proposalFolderUrl: proposalFolder.webUrl } : {}),
            });
          } catch (exErr) {
            // Non-fatal — proposal folder still landed.
            // eslint-disable-next-line no-console
            console.warn(`[OneDrive] bulk auto-link execution failed for ${p.code}`, exErr);
          }
        }
      } catch (err) {
        progress.failed++;
        const msg = err instanceof Error ? err.message : String(err);
        progress.failures.push(`${p.code}: ${msg}`);
        // eslint-disable-next-line no-console
        console.warn(`[OneDrive] bulk auto-link failed for ${p.code}`, err);
      }
      progress.done++;
      setBulkLinkProgress({ ...progress });
    }
    progress.running = false;
    progress.currentCode = '';
    setBulkLinkProgress({ ...progress });
    const summary = `Done: ${progress.linked} linked to existing, ${progress.created} created, ${progress.failed} failed.`;
    setBulkLinkSummary(summary);
  };
  // "New project" form lives in NewCalcsheetProjectDialog (shared with the
  // Whiteboard).
  const [open, setOpen] = useState(false);

  // ── filter + sort state ────────────────────────────────────────────────────
  const [initPrefs] = useState(loadListPrefs);
  const [search, setSearch] = useState(initPrefs.search);
  const [statusFilter, setStatusFilter] = useState<ProjectStatus[]>(initPrefs.statusFilter);
  const [customerFilter, setCustomerFilter] = useState<string>(initPrefs.customerFilter);
  const [yearFilter, setYearFilter] = useState<string>(initPrefs.yearFilter);
  const [legacyFilter, setLegacyFilter] = useState<'all' | 'legacy' | 'current'>(initPrefs.legacyFilter);
  const [ongoingOnly, setOngoingOnly] = useState(initPrefs.ongoingOnly);
  const [hideInactive, setHideInactive] = useState(initPrefs.hideInactive);
  const [sortKey, setSortKey] = useState<SortKey>(initPrefs.sortKey);
  const [sortDir, setSortDir] = useState<SortDir>(initPrefs.sortDir);

  useEffect(() => {
    saveListPrefs({
      sortKey, sortDir, search, statusFilter, customerFilter, yearFilter, legacyFilter, ongoingOnly, hideInactive,
    });
  }, [sortKey, sortDir, search, statusFilter, customerFilter, yearFilter, legacyFilter, ongoingOnly, hideInactive]);

  // OneDrive is no longer a hard gate on project creation. When it's configured
  // but the user isn't signed in, we still let them create the project and link
  // the proposal folder later. This avoids stranding a colleague whose MSAL
  // session is cached but can't acquire a token silently (24h SPA refresh-token
  // cap + ssoSilent blocked by browser cookie policy / wrong origin).
  const startNew = () => setOpen(true);

  // Memoize per-project data so sort/filter doesn't re-scan quotations N times per render
  const enriched = useMemo(() => projects.map((p) => {
    const customer = clients.find((c) => c.id === p.customerId);
    const partner = clients.find((c) => c.id === p.partnerId);
    const qs = quotations.filter((q) => q.projectId === p.id);
    const line = (q: Quotation) => ({
      kind: q.kind,
      revision: q.revision || '00',
      total: salesAccountedTotal(q, computeTotals(q)),
      servicesOnly: q.salesValueScope === 'services_only',
    });
    // One line per kind (IOCT first): the awarded quotation when the project was
    // won on that kind, otherwise the highest revision. The rest go in a tooltip.
    const current: Partial<Record<QuotationKind, Quotation>> = {};
    const kinds = Array.from(new Set<QuotationKind>(['IOCT', 'ACTI', ...qs.map((q) => q.kind)]));
    const totals = kinds.flatMap((kind) => {
      const revs = qs
        .filter((q) => q.kind === kind)
        .sort((a, b) => (b.revision || '00').localeCompare(a.revision || '00'));
      if (revs.length === 0) return [];
      const shown = revs.find((q) => q.id === p.awarded?.quotationId) ?? revs[0];
      current[kind] = shown;
      return [{ ...line(shown), others: revs.filter((q) => q !== shown).map(line) }];
    });
    const grandTotal = totals.reduce((sum, t) => Math.max(sum, t.total), 0);  // use max kind as the "headline"
    const hasLegacy = qs.some((q) => q.formulaVersion === 'legacy');
    const year = p.date ? new Date(p.date).getFullYear() : 0;
    // IOCT margin follows the IOCT line shown in the Sales column.
    const margin = current.IOCT ? ioctMargin(computeTotals(current.IOCT)) : null;
    return { p, customer, partner, totals, grandTotal, hasLegacy, year, margin };
  }), [projects, clients, quotations]);

  // Derive year options from data
  const yearOptions = useMemo(() => {
    const ys = Array.from(new Set(enriched.map((e) => e.year).filter((y) => y > 0))).sort((a, b) => b - a);
    return ys;
  }, [enriched]);

  // Filter
  const filtered = useMemo(() => {
    const s = search.trim().toLowerCase();
    return enriched.filter(({ p, customer, partner, hasLegacy, year }) => {
      if (s) {
        const hay = `${p.code} ${p.name} ${p.location ?? ''} ${customer?.name ?? ''} ${customer?.code ?? ''} ${partner?.name ?? ''}`.toLowerCase();
        if (!hay.includes(s)) return false;
      }
      // "Hide lost & inactive" (ticked by default) hides them unless that status
      // is explicitly picked in the Status filter.
      if (hideInactive && DEFAULT_HIDDEN_STATUSES.includes(p.status) && !statusFilter.includes(p.status)) return false;
      if (statusFilter.length > 0 && !statusFilter.includes(p.status)) return false;
      if (customerFilter !== 'all' && p.customerId !== customerFilter) return false;
      if (yearFilter !== 'all' && String(year) !== yearFilter) return false;
      if (legacyFilter === 'legacy' && !hasLegacy) return false;
      if (legacyFilter === 'current' && hasLegacy) return false;
      if (ongoingOnly && !p.ongoing) return false;
      return true;
    });
  }, [enriched, search, statusFilter, customerFilter, yearFilter, legacyFilter, ongoingOnly, hideInactive]);

  // Sort
  const sorted = useMemo(() => {
    const arr = [...filtered];
    const mul = sortDir === 'asc' ? 1 : -1;
    arr.sort((a, b) => {
      let av: string | number = '';
      let bv: string | number = '';
      switch (sortKey) {
        case 'code': av = a.p.code; bv = b.p.code; break;
        case 'name': av = (a.p.name || '').toLowerCase(); bv = (b.p.name || '').toLowerCase(); break;
        case 'customer': av = (a.customer?.name || '').toLowerCase(); bv = (b.customer?.name || '').toLowerCase(); break;
        case 'date': av = a.p.date || ''; bv = b.p.date || ''; break;
        case 'updatedAt': av = a.p.updatedAt || ''; bv = b.p.updatedAt || ''; break;
        case 'status': av = a.p.status; bv = b.p.status; break;
        case 'grade':
          // A/B/C rank (A best); ungraded sorts to the bottom regardless of direction.
          av = a.p.opportunityGrade ? GRADE_OPTIONS.indexOf(a.p.opportunityGrade) : Infinity;
          bv = b.p.opportunityGrade ? GRADE_OPTIONS.indexOf(b.p.opportunityGrade) : Infinity;
          break;
        case 'grandTotal': av = a.grandTotal; bv = b.grandTotal; break;
        case 'margin':
          // Sort by pct; nulls (no data) go to the bottom regardless of direction
          av = a.margin ? a.margin.pct : -Infinity;
          bv = b.margin ? b.margin.pct : -Infinity;
          break;
      }
      if (av < bv) return -1 * mul;
      if (av > bv) return 1 * mul;
      return 0;
    });
    return arr;
  }, [filtered, sortKey, sortDir]);

  const toggleSort = (key: SortKey) => {
    const nextDir: SortDir = sortKey === key
      ? (sortDir === 'asc' ? 'desc' : 'asc')
      : (key === 'date' || key === 'updatedAt' || key === 'grandTotal' ? 'desc' : 'asc');
    setSortKey(key);
    setSortDir(nextDir);
  };

  // ── scroll-position memory + last-clicked row highlight ────────────────────
  const SCROLL_KEY = 'calcsheet-projects-scroll';
  const LAST_KEY = 'calcsheet-projects-last';
  const saveScroll = (projectId?: string) => {
    sessionStorage.setItem(SCROLL_KEY, String(window.scrollY));
    if (projectId) sessionStorage.setItem(LAST_KEY, projectId);
  };

  // The most recently clicked project id — persists across visits to the
  // detail page so the user can see at-a-glance which row they just came
  // back from. Cleared automatically when they click a different row.
  const [lastClickedId, setLastClickedId] = useState<string>(() =>
    sessionStorage.getItem(LAST_KEY) || '',
  );

  useEffect(() => {
    const saved = sessionStorage.getItem(SCROLL_KEY);
    if (!saved) return;
    sessionStorage.removeItem(SCROLL_KEY);
    const top = parseInt(saved, 10);
    // Double-rAF: first frame commits layout, second frame is safe to scroll
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        window.scrollTo({ top, behavior: 'instant' as ScrollBehavior });
      });
    });
  }, []);

  const clearFilters = () => {
    setSearch(''); setStatusFilter([]); setCustomerFilter('all');
    setYearFilter('all'); setLegacyFilter('all'); setOngoingOnly(false); setHideInactive(true);
  };

  const handleExportList = async () => {
    setExportingList(true);
    try {
      await exportProjectListXlsx(sorted.map(({ p, customer, partner }) => ({
        code: p.code,
        name: p.name,
        customer: customer?.name ?? '',
        partner: partner?.name ?? '',
        date: p.date ? format(new Date(p.date), 'dd MMM yyyy') : '',
        status: p.status,
        ongoing: p.ongoing ? 'Yes' : '—',
        notes: p.notes ?? '',
        createdBy: p.createdByName ?? '',
      })));
    } catch (err) {
      setCreateNotice({ severity: 'error', message: err instanceof Error ? err.message : 'Export failed.' });
    } finally {
      setExportingList(false);
    }
  };

  const anyFilterActive = search || statusFilter.length > 0 || customerFilter !== 'all'
    || yearFilter !== 'all' || legacyFilter !== 'all' || ongoingOnly;

  // Filters tucked behind the filter button on cards (search stays visible, so it doesn't count).
  const hasHiddenFilters = statusFilter.length > 0 || customerFilter !== 'all' || yearFilter !== 'all'
    || legacyFilter !== 'all' || ongoingOnly;

  const SortHeader = ({ k, label, align = 'left' }: { k: SortKey; label: string; align?: 'left' | 'right' }) => (
    <TableCell align={align} sortDirection={sortKey === k ? sortDir : false}>
      <TableSortLabel
        active={sortKey === k}
        direction={sortKey === k ? sortDir : 'asc'}
        onClick={() => toggleSort(k)}
      >
        {label}
      </TableSortLabel>
    </TableCell>
  );

  const filterControls = (
    <>
          <FormControl size="small" sx={{ minWidth: 170 }}>
            <InputLabel>Status</InputLabel>
            <Select
              multiple
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value as ProjectStatus[])}
              input={<OutlinedInput label="Status" />}
              renderValue={(selected) =>
                selected.length === STATUS_OPTIONS.length
                  ? 'All'
                  : selected.map(statusLabel).join(', ')
              }
            >
              {STATUS_OPTIONS.map((status) => (
                <MenuItem key={status} value={status}>
                  <Checkbox size="small" checked={statusFilter.includes(status)} />
                  <ListItemText primary={statusLabel(status)} />
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <TextField
            select size="small" label="Customer" value={customerFilter}
            onChange={(e) => setCustomerFilter(e.target.value)}
            sx={{ minWidth: 180 }}
          >
            <MenuItem value="all">All</MenuItem>
            {clients
              .filter((c) => enriched.some((e) => e.p.customerId === c.id))
              .sort((a, b) => (a.code || a.name).localeCompare(b.code || b.name))
              .map((c) => (
                <MenuItem key={c.id} value={c.id}>{c.code ? `${c.code} — ${c.name}` : c.name}</MenuItem>
              ))}
          </TextField>
          <TextField
            select size="small" label="Year" value={yearFilter}
            onChange={(e) => setYearFilter(e.target.value)}
            sx={{ minWidth: 100 }}
          >
            <MenuItem value="all">All</MenuItem>
            {yearOptions.map((y) => <MenuItem key={y} value={String(y)}>{y}</MenuItem>)}
          </TextField>
          <TextField
            select size="small" label="Formula" value={legacyFilter}
            onChange={(e) => setLegacyFilter(e.target.value as 'all' | 'legacy' | 'current')}
            sx={{ minWidth: 120 }}
          >
            <MenuItem value="all">All</MenuItem>
            <MenuItem value="legacy">Legacy only</MenuItem>
            <MenuItem value="current">Current only</MenuItem>
          </TextField>
          <FormControlLabel
            control={<Switch size="small" checked={ongoingOnly} onChange={(e) => setOngoingOnly(e.target.checked)} />}
            label={<Typography variant="caption">Active proposals only</Typography>}
          />
          <FormControlLabel
            control={<Switch size="small" checked={hideInactive} onChange={(e) => setHideInactive(e.target.checked)} />}
            label={<Typography variant="caption">Hide lost &amp; inactive</Typography>}
          />
    </>
  );

  return (
    <Stack spacing={2}>
      <Stack direction="row" flexWrap="wrap" useFlexGap rowGap={1} columnGap={2} alignItems="center" justifyContent="space-between">
        <Typography variant="h5" sx={{ fontWeight: 600 }}>Projects</Typography>
        <Stack direction="row" flexWrap="wrap" useFlexGap spacing={1}>
          {isCorporateOneDriveConfigured() && unlinkedProjects.length > 0 && (
            <Tooltip title={`Scan OneDrive and link the ${unlinkedProjects.length} project${unlinkedProjects.length === 1 ? '' : 's'} with no folder yet`}>
              <Button
                variant="outlined"
                color="info"
                startIcon={<CloudSyncIcon />}
                onClick={() => setBulkLinkDialogOpen(true)}
                disabled={bulkLinkProgress?.running}
              >
                Auto-link OneDrive ({unlinkedProjects.length})
              </Button>
            </Tooltip>
          )}
          {oneDriveRequired && !oneDriveSignedIn && (
            <Button
              variant="outlined"
              color="info"
              startIcon={<CloudSyncIcon />}
              onClick={() => { void oneDriveLogin(); }}
              disabled={oneDriveLoading}
            >
              Sign in OneDrive
            </Button>
          )}
          <Button
            variant="outlined"
            startIcon={<FileDownloadIcon />}
            onClick={() => { void handleExportList(); }}
            disabled={exportingList || sorted.length === 0}
          >
            Export list
          </Button>
          <Button
            component={Link}
            to="/sales/calcsheet/import-legacy"
            variant="outlined"
            color="warning"
            startIcon={<UploadFileIcon />}
          >
            Import legacy
          </Button>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => { void startNew(); }}
            disabled={oneDriveRequired && !oneDriveSignedIn && oneDriveLoading}
          >
            New project
          </Button>
        </Stack>
      </Stack>

      {/* Bulk auto-link progress */}
      {bulkLinkProgress && bulkLinkProgress.running && (
        <Paper sx={{ p: 2 }}>
          <Stack spacing={1}>
            <Typography variant="body2" sx={{ fontWeight: 500 }}>
              Auto-linking OneDrive folders… {bulkLinkProgress.done} / {bulkLinkProgress.total}
              {bulkLinkProgress.currentCode && ` · ${bulkLinkProgress.currentCode}`}
            </Typography>
            <LinearProgress
              variant="determinate"
              value={bulkLinkProgress.total ? (bulkLinkProgress.done / bulkLinkProgress.total) * 100 : 0}
            />
            <Typography variant="caption" color="text.secondary">
              Linked to existing: {bulkLinkProgress.linked} · Newly created: {bulkLinkProgress.created} · Failed: {bulkLinkProgress.failed}
            </Typography>
          </Stack>
        </Paper>
      )}
      {bulkLinkProgress && !bulkLinkProgress.running && bulkLinkProgress.failures.length > 0 && (
        <Alert severity="warning" onClose={() => setBulkLinkProgress(null)}>
          <Typography variant="body2" sx={{ fontWeight: 500 }}>{bulkLinkProgress.failed} project{bulkLinkProgress.failed === 1 ? '' : 's'} failed:</Typography>
          <Box component="ul" sx={{ m: 0, pl: 2, fontSize: '0.8rem' }}>
            {bulkLinkProgress.failures.slice(0, 10).map((f, i) => <li key={i}>{f}</li>)}
            {bulkLinkProgress.failures.length > 10 && <li>… and {bulkLinkProgress.failures.length - 10} more (see console)</li>}
          </Box>
        </Alert>
      )}

      <Dialog open={bulkLinkDialogOpen} onClose={() => setBulkLinkDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Auto-link OneDrive folders</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            For each of the <strong>{unlinkedProjects.length}</strong> project{unlinkedProjects.length === 1 ? '' : 's'} without
            a OneDrive folder linked yet, the system will:
          </Typography>
          <Box component="ol" sx={{ pl: 3, my: 0, fontSize: '0.875rem', color: 'text.secondary' }}>
            <li>Look in <code>{/* eslint-disable-next-line */}'00 Proposal/IO Proposal'</code> for a folder whose name starts with the project's PCS code</li>
            <li>If exactly one match is found, link to it (no folder created)</li>
            <li>If no match, create a new folder using the canonical name</li>
            <li>For projects already marked <strong>won</strong>, also link or create the execution folder</li>
          </Box>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
            Existing folders are <strong>never modified or deleted</strong>. Worst-case outcome is an extra empty
            folder which you can delete manually. This may take a minute or two.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setBulkLinkDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" startIcon={<CloudSyncIcon />} onClick={runBulkAutoLink}>
            Start
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={!!bulkLinkSummary}
        autoHideDuration={8000}
        onClose={() => setBulkLinkSummary('')}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setBulkLinkSummary('')}
          severity={bulkLinkProgress?.failed ? 'warning' : 'success'}
          variant="filled"
          sx={{ width: '100%' }}
        >
          {bulkLinkSummary}
        </Alert>
      </Snackbar>

      <Snackbar
        open={!!createNotice}
        autoHideDuration={createNotice?.severity === 'error' ? 12000 : 9000}
        onClose={() => setCreateNotice(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setCreateNotice(null)}
          severity={createNotice?.severity ?? 'info'}
          variant="filled"
          sx={{ width: '100%' }}
        >
          {createNotice?.message}
        </Alert>
      </Snackbar>

      {/* Filter bar */}
      {isCards ? (
        <Paper sx={{ p: 1.5 }} variant="outlined">
          <Stack spacing={1}>
            <Stack direction="row" spacing={1} alignItems="center">
              <TextField
                size="small" fullWidth placeholder="Search code, name, customer…"
                value={search} onChange={(e) => setSearch(e.target.value)}
                InputProps={{
                  startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment>,
                  endAdornment: search ? (
                    <InputAdornment position="end">
                      <IconButton size="small" onClick={() => setSearch('')} aria-label="Clear search"><ClearIcon fontSize="small" /></IconButton>
                    </InputAdornment>
                  ) : null,
                }}
              />
              <IconButton onClick={() => setFiltersOpen((v) => !v)} aria-label="Filters and sort" color={filtersOpen ? 'primary' : 'default'}>
                <Badge color="primary" variant="dot" invisible={!hasHiddenFilters}>
                  <FilterListIcon />
                </Badge>
              </IconButton>
            </Stack>
            <Collapse in={filtersOpen} unmountOnExit>
              <Stack direction="row" spacing={1.5} alignItems="center" flexWrap="wrap" useFlexGap sx={{ pt: 0.5 }}>
                <Stack direction="row" spacing={0.5} alignItems="center" sx={{ width: '100%' }}>
                  <TextField
                    select size="small" label="Sort by" value={sortKey} sx={{ flex: 1 }}
                    onChange={(e) => {
                      const k = e.target.value as SortKey;
                      setSortKey(k);
                      setSortDir(k === 'date' || k === 'updatedAt' || k === 'grandTotal' || k === 'margin' ? 'desc' : 'asc');
                    }}
                  >
                    <MenuItem value="updatedAt">Last edited</MenuItem>
                    <MenuItem value="date">Date</MenuItem>
                    <MenuItem value="code">Code</MenuItem>
                    <MenuItem value="name">Project</MenuItem>
                    <MenuItem value="customer">Customer</MenuItem>
                    <MenuItem value="status">Status</MenuItem>
                    <MenuItem value="grandTotal">Sales</MenuItem>
                    <MenuItem value="margin">IOCT Margin</MenuItem>
                  </TextField>
                  <IconButton onClick={() => setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'))} aria-label={sortDir === 'asc' ? 'Ascending' : 'Descending'}>
                    {sortDir === 'asc' ? <ArrowUpwardIcon fontSize="small" /> : <ArrowDownwardIcon fontSize="small" />}
                  </IconButton>
                </Stack>
                {filterControls}
              </Stack>
            </Collapse>
            <Stack direction="row" alignItems="center" justifyContent="space-between">
              <Typography variant="caption" color="text.secondary">{sorted.length} of {projects.length}</Typography>
              {anyFilterActive && (
                <Button size="small" onClick={clearFilters} startIcon={<ClearIcon />}>Clear</Button>
              )}
            </Stack>
          </Stack>
        </Paper>
      ) : (
      <Paper sx={{ p: 1.5 }} variant="outlined">
        <Stack direction="row" spacing={1.5} alignItems="center" flexWrap="wrap" useFlexGap>
          <TextField
            size="small"
            placeholder="Search code, name, customer…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            sx={{ minWidth: { xs: '100%', sm: 280 }, flex: '1 1 280px', maxWidth: 380 }}
            InputProps={{
              startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment>,
              endAdornment: search ? (
                <InputAdornment position="end">
                  <IconButton size="small" onClick={() => setSearch('')}><ClearIcon fontSize="small" /></IconButton>
                </InputAdornment>
              ) : null,
            }}
          />
          {filterControls}
          <Box sx={{ flex: 1 }} />
          <Typography variant="caption" color="text.secondary">
            {sorted.length} of {projects.length}
          </Typography>
          {anyFilterActive && (
            <Button size="small" onClick={clearFilters} startIcon={<ClearIcon />}>Clear</Button>
          )}
        </Stack>
      </Paper>
      )}

      {isCards ? (
        <Box sx={{ display: 'grid', gap: 1, gridTemplateColumns: 'minmax(0, 1fr)' }}>
          {sorted.map(({ p, customer, totals, hasLegacy, margin }) => {
            const isLast = p.id === lastClickedId;
            return (
              <Paper
                key={p.id} variant="outlined" component={Link} to={`/sales/calcsheet/projects/${p.id}`}
                onClick={() => { setLastClickedId(p.id); saveScroll(p.id); }}
                sx={{
                  display: 'flex', flexDirection: 'column', justifyContent: 'space-between', p: 1.5, textDecoration: 'none', color: 'inherit',
                  '&:active': { bgcolor: 'action.hover' },
                  ...(isLast ? { bgcolor: 'rgba(25, 118, 210, 0.08)', borderLeft: '3px solid', borderLeftColor: 'primary.main' } : {}),
                }}
              >
                <Stack direction="row" spacing={1} alignItems="flex-start">
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Stack direction="row" spacing={0.75} alignItems="center" flexWrap="wrap" useFlexGap>
                      <Typography variant="caption" sx={{ fontFamily: 'monospace', color: 'text.secondary' }}>
                        {p.code || 'No code yet'}
                      </Typography>
                      {hasLegacy && <HistoryIcon color="warning" sx={{ fontSize: '0.85rem' }} />}
                      {isLast && <Chip size="small" label="Last" color="primary" variant="outlined" sx={{ height: 16, '& .MuiChip-label': { px: 0.75, fontSize: '0.65rem' } }} />}
                    </Stack>
                    <Typography variant="body2" sx={{ fontWeight: 600, mt: 0.25 }}>{p.name}</Typography>
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                      {customer?.name ?? '—'}{p.location ? ` · ${p.location}` : ''}
                    </Typography>
                  </Box>
                  <Chip size="small" label={statusLabel(p.status)} color={statusColors[p.status]} sx={{ flexShrink: 0 }} />
                  <ChevronRightIcon fontSize="small" sx={{ color: 'text.disabled', mt: 0.25, flexShrink: 0 }} />
                </Stack>
                <Stack direction="row" justifyContent="space-between" alignItems="flex-end" spacing={1} sx={{ mt: 1 }}>
                  <Stack spacing={0.25}>
                    {totals.map((t, i) => (
                      <Typography key={i} variant="caption" sx={{ fontFamily: 'monospace' }}>
                        <strong>{t.kind} R{t.revision}:</strong> {PHP(t.total)}{t.servicesOnly ? ' · services only' : ''}
                        {t.others.length > 0 && (
                          <Box component="span" sx={{ color: 'text.secondary' }}> (+{t.others.length} more)</Box>
                        )}
                      </Typography>
                    ))}
                    {totals.length === 0 && <Typography variant="caption" color="text.secondary">No quotation yet</Typography>}
                  </Stack>
                  <Box sx={{ textAlign: 'right' }}>
                    {margin ? (
                      <>
                        <Typography variant="caption" sx={{ display: 'block', fontFamily: 'monospace', fontWeight: 600, color: margin.value >= 0 ? 'success.main' : 'error.main' }}>
                          {PHP(margin.value)}
                        </Typography>
                        <Typography variant="caption" color="text.secondary" sx={{ fontFamily: 'monospace' }}>
                          {margin.pct.toFixed(1)}% margin
                        </Typography>
                      </>
                    ) : (
                      <Typography variant="caption" color="text.secondary">Margin —</Typography>
                    )}
                  </Box>
                </Stack>
              </Paper>
            );
          })}
          {sorted.length === 0 && (
            <Paper variant="outlined" sx={{ p: 3, textAlign: 'center', color: 'text.secondary', gridColumn: '1 / -1' }}>
              <Typography variant="body2">
                {projects.length === 0 ? 'No projects yet — tap "New project" to start' : 'No projects match the current filters'}
              </Typography>
            </Paper>
          )}
        </Box>
      ) : (
      <Paper>
        <Box sx={{ overflowX: 'auto' }}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <SortHeader k="code" label="Code" />
              <SortHeader k="name" label="Project" />
              <SortHeader k="customer" label="Customer" />
              <TableCell>Partner</TableCell>
              <TableCell>Created by</TableCell>
              <SortHeader k="date" label="Date" />
              <SortHeader k="updatedAt" label="Last edited" />
              <SortHeader k="status" label="Status" />
              <SortHeader k="grade" label="Grade" />
              <SortHeader k="grandTotal" label="Sales" align="right" />
              <SortHeader k="margin" label="IOCT Margin" align="right" />
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {sorted.map(({ p, customer, partner, totals, hasLegacy, margin }) => {
              const isLast = p.id === lastClickedId;
              const onRowClick = () => { setLastClickedId(p.id); saveScroll(p.id); };
              return (
              <TableRow
                key={p.id}
                hover
                sx={isLast ? {
                  // Soft highlight + accent border on the most recently visited row.
                  // sessionStorage-backed so it survives the round-trip into the
                  // project detail page during folder-backfill workflows.
                  bgcolor: 'rgba(25, 118, 210, 0.08)',
                  borderLeft: '3px solid',
                  borderLeftColor: 'primary.main',
                } : undefined}
              >
                <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.75rem', whiteSpace: 'nowrap' }}>
                  <Stack direction="row" spacing={0.5} alignItems="center">
                    <Link to={`/sales/calcsheet/projects/${p.id}`} style={{ color: p.code ? 'inherit' : undefined }} onClick={onRowClick}>
                      {p.code || <Typography component="span" variant="inherit" color="text.secondary" sx={{ fontStyle: 'italic' }}>No code yet</Typography>}
                    </Link>
                    {hasLegacy && (
                      <Tooltip title="Has legacy quotation(s)">
                        <HistoryIcon fontSize="inherit" color="warning" sx={{ fontSize: '0.85rem' }} />
                      </Tooltip>
                    )}
                    {isLast && (
                      <Tooltip title="Last visited">
                        <Chip size="small" label="Last" color="primary" variant="outlined" sx={{ height: 16, '& .MuiChip-label': { px: 0.75, fontSize: '0.65rem' } }} />
                      </Tooltip>
                    )}
                  </Stack>
                </TableCell>
                <TableCell>
                  <Link to={`/sales/calcsheet/projects/${p.id}`} style={{ color: 'inherit', textDecoration: 'none' }} onClick={onRowClick}>
                    <Box>
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>{p.name}</Typography>
                      {p.location && <Typography variant="caption" color="text.secondary">{p.location}</Typography>}
                    </Box>
                  </Link>
                </TableCell>
                <TableCell>{customer?.name ?? '—'}</TableCell>
                <TableCell>{partner?.name ?? '—'}</TableCell>
                <TableCell>{p.createdByName ?? '—'}</TableCell>
                <TableCell>{p.date ? format(new Date(p.date), 'dd MMM yyyy') : '—'}</TableCell>
                <TableCell>{p.updatedAt ? format(new Date(p.updatedAt), 'dd MMM yyyy HH:mm') : '—'}</TableCell>
                <TableCell>
                  <Stack direction="row" spacing={0.5} alignItems="center">
                    <Select
                      size="small"
                      value={p.status}
                      onChange={(e) => {
                        const nextStatus = e.target.value as ProjectStatus;
                        void (async () => {
                          // Marking won from the list must carry a Project List number so
                          // OneDrive gets `IOCT####-CUST Name` (not a leftover PCS folder).
                          if (nextStatus === 'won' && p.status !== 'won') {
                            try {
                              let mainProjectId = p.mainProjectId;
                              let mainProjectNo = p.mainProjectNo;
                              if (!mainProjectNo) {
                                const result = await syncMainProject(p.id, { force: false });
                                mainProjectId = result.mainProjectId;
                                mainProjectNo = result.projectNo;
                              }
                              await updateProject(p.id, {
                                status: 'won',
                                ...(mainProjectId ? { mainProjectId } : {}),
                                ...(mainProjectNo ? { mainProjectNo } : {}),
                              });
                              setCreateNotice({
                                severity: 'info',
                                message: mainProjectNo
                                  ? `Marked won · Project List ${mainProjectNo}. OneDrive folder: ${executionProjectFolderName({ code: p.code, name: p.name, mainProjectNo })}`
                                  : 'Marked won. Link a Project List record so the OneDrive folder can use the IOCT project number.',
                              });
                            } catch (err) {
                              setCreateNotice({
                                severity: 'error',
                                message: err instanceof Error ? err.message : 'Failed to mark project as won.',
                              });
                            }
                            return;
                          }
                          await updateProject(p.id, { status: nextStatus });
                        })();
                      }}
                      sx={{
                        minWidth: 96,
                        '& .MuiSelect-select': { py: 0.25, display: 'flex', alignItems: 'center' },
                        '& .MuiOutlinedInput-notchedOutline': { border: 'none' },
                        '&:hover .MuiOutlinedInput-notchedOutline': { border: '1px solid', borderColor: 'grey.400' },
                        '&.Mui-focused .MuiOutlinedInput-notchedOutline': { border: '1px solid', borderColor: 'primary.main' },
                      }}
                      MenuProps={{ sx: { '& .MuiMenuItem-root': { fontSize: '0.8rem' } } }}
                    >
                      {STATUS_OPTIONS.map((s) => (
                        <MenuItem key={s} value={s} dense>
                          <Chip size="small" label={statusLabel(s)} color={statusColors[s]} sx={{ minWidth: 60 }} />
                        </MenuItem>
                      ))}
                    </Select>
                    {p.ongoing && <Chip size="small" label="active" variant="outlined" sx={{ height: 18 }} />}
                    {p.status === 'won' && !!p.customerPOs?.length && (
                      <Tooltip title={`${p.customerPOs.length} customer PO(s) attached`}>
                        <Chip size="small" label="PO" color="success" variant="outlined" sx={{ height: 18 }} />
                      </Tooltip>
                    )}
                  </Stack>
                </TableCell>
                <TableCell>
                  <Select
                    size="small"
                    displayEmpty
                    value={p.opportunityGrade ?? ''}
                    onChange={(e) => updateProject(p.id, { opportunityGrade: (e.target.value || undefined) as OpportunityGrade | undefined })}
                    sx={{
                      minWidth: 76,
                      '& .MuiSelect-select': { py: 0.25, display: 'flex', alignItems: 'center' },
                      '& .MuiOutlinedInput-notchedOutline': { border: 'none' },
                      '&:hover .MuiOutlinedInput-notchedOutline': { border: '1px solid', borderColor: 'grey.400' },
                      '&.Mui-focused .MuiOutlinedInput-notchedOutline': { border: '1px solid', borderColor: 'primary.main' },
                    }}
                    MenuProps={{ sx: { '& .MuiMenuItem-root': { fontSize: '0.8rem' } } }}
                    renderValue={(v) => (
                      v ? <Chip size="small" label={v as string} color={gradeColors[v as OpportunityGrade]} sx={{ minWidth: 32, fontWeight: 700 }} />
                        : <Chip size="small" label="Ungraded" variant="outlined" sx={{ minWidth: 32 }} />
                    )}
                  >
                    <MenuItem value="" dense>
                      <em>Ungraded</em>
                    </MenuItem>
                    {GRADE_OPTIONS.map((g) => (
                      <MenuItem key={g} value={g} dense>
                        <Stack direction="row" spacing={1} alignItems="center">
                          <Chip size="small" label={g} color={gradeColors[g]} sx={{ minWidth: 32, fontWeight: 700 }} />
                          <Typography variant="caption">{opportunityGradeLabel(g).replace(`${g} — `, '')}</Typography>
                        </Stack>
                      </MenuItem>
                    ))}
                  </Select>
                </TableCell>
                <TableCell align="right">
                  <Stack spacing={0.25}>
                    {totals.map((t, i) => (
                      <Typography key={i} variant="caption" sx={{ fontFamily: 'monospace' }}>
                        <strong>{t.kind} R{t.revision}:</strong> {PHP(t.total)}
                        {t.servicesOnly ? ' · services only' : ''}
                        {t.others.length > 0 && ' '}
                        {t.others.length > 0 && (
                          <Tooltip
                            title={
                              <Box sx={{ fontFamily: 'monospace' }}>
                                {t.others.map((o, j) => (
                                  <div key={j}>R{o.revision}  {PHP(o.total)}{o.servicesOnly ? ' · services only' : ''}</div>
                                ))}
                              </Box>
                            }
                          >
                            <Box component="span" sx={{ color: 'text.secondary', cursor: 'help', textDecoration: 'underline dotted' }}>
                              (+{t.others.length} more)
                            </Box>
                          </Tooltip>
                        )}
                      </Typography>
                    ))}
                    {totals.length === 0 && <Typography variant="caption" color="text.secondary">none</Typography>}
                  </Stack>
                </TableCell>
                <TableCell align="right">
                  {margin ? (
                    <Stack spacing={0.25}>
                      <Typography
                        variant="caption"
                        sx={{
                          fontFamily: 'monospace',
                          fontWeight: 600,
                          color: margin.value >= 0 ? 'success.main' : 'error.main',
                        }}
                      >
                        {PHP(margin.value)}
                      </Typography>
                      <Typography variant="caption" color="text.secondary" sx={{ fontFamily: 'monospace' }}>
                        {margin.pct.toFixed(1)}%
                      </Typography>
                    </Stack>
                  ) : (
                    <Tooltip title="No cost data — legacy snapshot was imported from PDF or an ACTI-variant workbook">
                      <Typography variant="caption" color="text.secondary">—</Typography>
                    </Tooltip>
                  )}
                </TableCell>
                <TableCell align="right">
                  <IconButton size="small" onClick={() => setDeleteTarget(p)}><DeleteIcon fontSize="small" /></IconButton>
                </TableCell>
              </TableRow>
              );
            })}
            {sorted.length === 0 && (
              <TableRow>
                <TableCell colSpan={10} align="center" sx={{ color: 'text.secondary', py: 4 }}>
                  {projects.length === 0
                    ? 'No projects yet — click "New project" to start'
                    : 'No projects match the current filters'}
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
        </Box>
      </Paper>
      )}

      <Dialog open={!!deleteTarget} onClose={() => setDeleteTarget(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Delete project?</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            This will permanently delete <strong>{deleteTarget?.code} — {deleteTarget?.name}</strong> and all of its quotations. This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteTarget(null)}>Cancel</Button>
          <Button
            variant="contained"
            color="error"
            onClick={() => {
              if (deleteTarget) { deleteProject(deleteTarget.id); setDeleteTarget(null); }
            }}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      <NewCalcsheetProjectDialog
        open={open}
        onClose={() => setOpen(false)}
        onCreated={(_project, notice) => { if (notice) setCreateNotice(notice); }}
      />
    </Stack>
  );
}
